import {Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {FeatureFlagService, SessionSubject} from '@emma-labs/shared/angular';
import {UserRole} from '@emma-labs/shared/typescript';
import {UntilDestroy} from '@ngneat/until-destroy';
import {BehaviorSubject, delay, Observable, of, throwError as observableThrowError} from 'rxjs';
import {
  catchError,
  defaultIfEmpty,
  first,
  map,
  mergeMap,
  publishLast,
  refCount,
  take,
  tap,
} from 'rxjs/operators';
import {AgentLoginResult} from '../entities/agent-login-result';
import {AgentSession} from '../entities/agent-session';
import {FADE_ANIMATION_TIME} from '../utils/utility-constants';
import {AgentSessionApiService} from './agent-session-api.service';
import {LocalKeyStorage} from './local-key-storage.service';

const AUTH_ERROR_MESSAGE = 'This action requires authentication.';

@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  public logout$: Observable<boolean>;
  private _logout$ = new BehaviorSubject(false);
  private agentSessionSubject = new SessionSubject<AgentSession>();

  constructor(
    private readonly api: AgentSessionApiService,
    private readonly keyStorage: LocalKeyStorage,
    private readonly router: Router,
    private readonly featureFlagService: FeatureFlagService,
  ) {
    const storedAgentKey = this.keyStorage.getAgentSessionId();

    if (storedAgentKey) {
      const observable = this.api.getAgentSession(storedAgentKey);
      this.saveAgentSessionFromObservable(observable).subscribe();
    }

    this.logout$ = this._logout$.asObservable();
  }

  login(email: string, password: string): Observable<AgentLoginResult> {
    return this.api.login(email, password).pipe(
      tap((result) => {
        if (result.success && result.session) {
          this.saveAgentSessionFromObservable(of(result.session));
        }
      }),
    );
  }

  validateMultifactorAuthenticationCode(
    email: string,
    password: string,
    code: string,
  ): Observable<AgentLoginResult> {
    return this.api.validateMultifactorAuthenticationCode(email, password, code).pipe(
      first(),
      tap((result) => {
        if (result.success && result.session) {
          this.saveAgentSessionFromObservable(of(result.session));
        }
      }),
    );
  }

  resendMultifactorAuthenticationCode(email: string): Observable<undefined> {
    return this.api.resendMultifactorAuthenticationCode(email).pipe(first());
  }

  handleSuccessfulLoginRedirection(session: AgentSession): void {
    this.featureFlagService
      .reinitialize(atob(session.agent.id))
      .pipe(first(), delay(FADE_ANIMATION_TIME))
      .subscribe(() => {
        switch (session.agent.role) {
          case UserRole.UNDERWRITER:
          case UserRole.CASE_MANAGER: {
            this.router.navigate(['/', 'dashboard', 'underwriting']);
            return;
          }
          default: {
            this.router.navigate(['/', 'dashboard', 'proposals']);
          }
        }
      });
  }

  logout(): Observable<undefined> {
    return this.currentAgentSessionStream.pipe(
      first(),
      mergeMap((session: AgentSession) => this.api.logout(session.id).pipe(catchError(() => of(undefined)))),
      tap(() => this.handleLogout()),
    );
  }

  handleLogout(): void {
    this._logout$.next(true);
    this.keyStorage.removeAgentSessionId();
    this.agentSessionSubject.next(undefined);
    this.featureFlagService.reinitialize();
    this.router.navigate(['/', 'auth']);
  }

  isAgentLoggedIn(): Observable<boolean> {
    return this.currentAgentSessionStream.pipe(
      first(),
      map((session) => session.agent && !session.invalid),
      defaultIfEmpty(false),
      catchError(() => of(false)),
    );
  }

  get currentAgentSessionOrAuthError(): Observable<AgentSession> {
    return this.currentAgentSessionStream.pipe(
      first(),
      mergeMap((session) => (session === undefined ? observableThrowError(AUTH_ERROR_MESSAGE) : of(session))),
    );
  }

  get currentAgentSessionStream(): Observable<AgentSession> {
    return this.agentSessionSubject.asObservable();
  }

  private saveAgentSessionFromObservable(observable: Observable<AgentSession>): Observable<AgentSession> {
    const hotObservable = observable.pipe(
      take(1),
      tap(
        (session) => {
          if (session.id) {
            this.keyStorage.setAgentSessionId(session.id);
          }
        },
        () => {
          this.keyStorage.removeAgentSessionId();
        },
      ),
      publishLast(),
      refCount(),
    );

    this.agentSessionSubject.nextAsync(hotObservable);
    return hotObservable;
  }
}
